<div class="tpf-page">
  <div class="tpf-save-bar">
    <div class="tpf-save-bar__inner">
      <div class="tpf-save-bar__left">
        <button
          type="button"
          class="tpf-save-bar__back"
          data-testid="tpf-back"
          [attr.aria-label]="'testPlans.form.back' | translate"
          (click)="onCancel()"
        >
          <mat-icon fontSet="material-icons-outlined" aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div class="tpf-save-bar__heading">
          <span class="tpf-save-bar__eyebrow">
            @if (isEdit && plan()) {
              {{ 'testPlans.form.eyebrowEdit' | translate: { id: plan()!.publicId } }}
            } @else {
              {{ 'testPlans.form.eyebrowNew' | translate }}
            }
          </span>
          <span class="tpf-save-bar__title">
            {{ titleKey() | translate }}
          </span>
        </div>
        @if (dirty() || selectionDirty() || paketStore.geaendert()) {
          <span class="tpf-dirty-pill" data-testid="tpf-dirty-pill">
            <span class="tpf-dirty-pill__dot" aria-hidden="true"></span>
            {{ 'testPlans.form.unsavedChanges' | translate }}
          </span>
        }
      </div>
      @if (!isReadOnly()) {
        <div class="tpf-save-bar__right">
          <button
            mat-stroked-button
            type="button"
            class="tpf-save-bar__cancel"
            data-testid="tpf-cancel"
            (click)="onCancel()"
          >
            {{ 'testPlans.form.cancel' | translate }}
          </button>
          <button
            mat-flat-button
            color="primary"
            type="button"
            class="tpf-save-bar__save"
            data-testid="tp-anlegen"
            [disabled]="saveDisabled()"
            (click)="onSave()"
          >
            <mat-icon aria-hidden="true">check</mat-icon>
            {{ (isEdit ? 'testPlans.form.save' : 'testPlans.form.saveCreate') | translate }}
          </button>
        </div>
      }
    </div>
  </div>

  @if (loading()) {
    <p class="tpf-hint">{{ 'testPlans.form.loading' | translate }}</p>
  } @else if (loadError()) {
    <p class="tpf-hint tpf-hint--error">{{ 'testPlans.form.loadError' | translate }}</p>
  } @else {
    @if (vorlageNotFound()) {
      <p class="tpf-hint tpf-hint--error" role="alert" data-testid="tp-vorlage-error">
        {{ 'testPlans.form.vorlageNotFound' | translate }}
      </p>
    }
    <form [formGroup]="form" class="tpf-body">
      <div class="tpf-main">
        <div class="tpf-card">
          <div class="tpf-card__body">
            <div class="tpf-section-title">
              <div class="tpf-section-title__title">{{ 'testPlans.form.basics.title' | translate }}</div>
              <div class="tpf-section-title__subtitle">{{ 'testPlans.form.basics.subtitle' | translate }}</div>
            </div>
            <div class="tpf-fields">
              <mat-form-field appearance="outline" class="tpf-field" subscriptSizing="dynamic">
                <mat-label>{{ 'testPlans.form.fields.title' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="title"
                  [placeholder]="'testPlans.form.fields.titlePlaceholder' | translate"
                  [attr.maxlength]="fieldLengths.NAME_MAX"
                  required
                  aria-required="true"
                />
                @if (form.controls.title.hasError('required') && form.controls.title.touched) {
                  <mat-error>{{ 'testPlans.form.fields.titleRequired' | translate }}</mat-error>
                }
              </mat-form-field>
              @if (releasesEmpty()) {
                <p class="tpf-release-empty" data-testid="release-empty-hint">
                  {{ 'testPlans.form.fields.releaseEmpty' | translate }}
                </p>
              } @else {
                <mat-form-field
                  appearance="outline"
                  class="tpf-field"
                  floatLabel="always"
                  subscriptSizing="dynamic"
                  data-testid="tp-release"
                >
                  <mat-label>{{ 'testPlans.form.fields.release' | translate }}</mat-label>
                  <mat-select
                    formControlName="releaseId"
                    data-testid="release-select"
                    required
                    [placeholder]="'testPlans.form.fields.releasePlaceholder' | translate"
                  >
                    @for (release of releases(); track release.id) {
                      <mat-option [value]="release.id">{{ release.id }}</mat-option>
                    }
                  </mat-select>
                  @if (form.controls.releaseId.hasError('required') && form.controls.releaseId.touched) {
                    <mat-error>{{ 'testPlans.form.fields.releaseRequired' | translate }}</mat-error>
                  }
                </mat-form-field>
              }
              <div class="tpf-kind-field" data-testid="tp-execution-kind">
                <span class="tpf-kind-field__label">
                  {{ 'testPlans.form.kind.label' | translate }}
                  <span class="tpf-kind-field__required" aria-hidden="true">*</span>
                </span>
                <app-segmented-filter-control
                  density="ausfuehrungsart"
                  [items]="[
                    {
                      id: 'MANUAL',
                      label: 'testPlans.form.kind.manual' | translate,
                      icon: 'manage_accounts',
                      testId: 'tp-kind-manual',
                    },
                    {
                      id: 'AUTOMATED',
                      label: 'testPlans.form.kind.automated' | translate,
                      icon: 'settings',
                      testId: 'tp-kind-automated',
                    },
                  ]"
                  [value]="planKind()"
                  [invalid]="isMixed()"
                  [disabled]="saving() || isReadOnly()"
                  [ariaLabel]="'testPlans.form.kind.label' | translate"
                  (valueChange)="onKindChange($event)"
                />
              </div>
            </div>
            <p class="tpf-kind-note">{{ 'testPlans.form.kind.note' | translate }}</p>
          </div>
        </div>

        <div class="tpf-steps" role="tablist" [attr.aria-label]="'testPlans.form.steps.aria' | translate">
          @for (step of steps; track step) {
            @let active = activeStep() === step;
            @let locked = step === 2 && !hasTestCases();
            @let done = step === 1 && !active && hasTestCases();
            <button
              type="button"
              class="tpf-step"
              role="tab"
              [id]="'tp-step-tab-' + step"
              [attr.data-testid]="'tp-step-tab-' + step"
              [class.tpf-step--active]="active"
              [class.tpf-step--locked]="locked"
              [attr.aria-selected]="active"
              [attr.aria-current]="active ? 'step' : null"
              [attr.aria-controls]="active ? 'tp-step-panel-' + step : null"
              [attr.aria-disabled]="locked || null"
              [attr.title]="
                locked
                  ? ((isReadOnly() ? 'testPlans.form.steps.lockedReadOnly' : 'testPlans.form.steps.locked') | translate)
                  : null
              "
              [attr.tabindex]="active ? 0 : -1"
              (click)="selectStep(step)"
              (keydown)="onStepKeydown($event, step)"
            >
              <span class="tpf-step__number" [class.tpf-step__number--done]="done">
                @if (done) {
                  <mat-icon aria-hidden="true">check</mat-icon>
                } @else {
                  {{ step }}
                }
              </span>
              <span class="tpf-step__label">
                {{ (step === 1 ? 'testPlans.form.steps.cases' : 'testPlans.form.steps.packages') | translate }}
              </span>
              <span class="tpf-step__info">
                @if (step === 1) {
                  {{ 'testPlans.form.steps.selected' | translate: { count: selectedCount() } }}
                } @else {
                  {{ 'testPlans.form.steps.optional' | translate }}
                }
              </span>
            </button>
          }
        </div>

        <div
          class="tpf-card"
          role="tabpanel"
          id="tp-step-panel-1"
          aria-labelledby="tp-step-tab-1"
          [hidden]="activeStep() !== 1"
        >
          <div class="tpf-card__body">
            <div class="tpf-section-title">
              <div class="tpf-section-title__title">
                {{ 'testPlans.form.picker.title' | translate }}
                <span class="tpf-section-title__required" aria-hidden="true">*</span>
              </div>
              @if (!testCasesLoadError()) {
                <div class="tpf-section-title__subtitle">
                  {{
                    (isReadOnly() ? 'testPlans.form.picker.subtitleReadOnly' : 'testPlans.form.picker.subtitleRequired')
                      | translate: { selected: selectedCount(), total: candidatesCount() }
                  }}
                </div>
              }
            </div>

            @if (isMixed()) {
              <div class="tpf-mixed-alert" role="alert" data-testid="tp-mixed-alert">
                <span class="tpf-mixed-alert__text">
                  <strong>{{ 'testPlans.form.mixed.title' | translate }}</strong>
                  {{
                    (isReadOnly() ? 'testPlans.form.mixed.textReadOnly' : 'testPlans.form.mixed.text')
                      | translate: { manual: executionCounts().manual, automated: executionCounts().automated }
                  }}
                </span>
                @if (!isReadOnly()) {
                  <button
                    mat-stroked-button
                    type="button"
                    data-testid="tp-keep-manual"
                    (click)="keepOnlyKind('MANUAL')"
                  >
                    {{ 'testPlans.form.mixed.keepManual' | translate }}
                  </button>
                  <button
                    mat-stroked-button
                    type="button"
                    data-testid="tp-keep-automated"
                    (click)="keepOnlyKind('AUTOMATED')"
                  >
                    {{ 'testPlans.form.mixed.keepAutomated' | translate }}
                  </button>
                }
              </div>
            } @else if (kindHint(); as hint) {
              <div class="tpf-kind-hint" role="alert" data-testid="tp-kind-hint">
                <span class="tpf-kind-hint__text">
                  <strong>{{ hint.key }}</strong>
                  {{
                    (caseExecutionKind(hint) === 'AUTOMATED'
                      ? 'testPlans.form.kind.hintAutomatedCase'
                      : 'testPlans.form.kind.hintManualCase'
                    ) | translate
                  }}
                </span>
                <button
                  type="button"
                  class="tpf-kind-hint__close"
                  [attr.aria-label]="'testPlans.form.kind.hintClose' | translate"
                  (click)="kindHint.set(null)"
                >
                  <mat-icon aria-hidden="true">close</mat-icon>
                </button>
              </div>
            }

            @if (addMode() && !testCasesLoadError()) {
              <div class="tpf-picker-filters">
                <div class="tpf-picker-search">
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">search</mat-icon>
                  <input
                    type="text"
                    [value]="search()"
                    (input)="onSearchInput($event)"
                    [placeholder]="'testPlans.form.picker.searchPlaceholder' | translate"
                    [attr.aria-label]="'testPlans.form.picker.searchAria' | translate"
                  />
                </div>
                @if (!planKind()) {
                  <select
                    class="tpf-picker-select"
                    [class.tpf-picker-select--active]="executionFilter()"
                    data-testid="tp-execution-filter"
                    [value]="executionFilter()"
                    (change)="onExecutionFilterChange($event)"
                    [attr.aria-label]="'testPlans.form.picker.executionAria' | translate"
                  >
                    <option value="">{{ 'testPlans.form.picker.executionAll' | translate }}</option>
                    <option value="MANUAL">{{ 'testPlans.form.picker.executionManual' | translate }}</option>
                    <option value="AUTOMATABLE">{{ 'testPlans.form.picker.executionAutomatable' | translate }}</option>
                    <option value="AUTOMATED">{{ 'testPlans.form.picker.executionAutomated' | translate }}</option>
                  </select>
                } @else {
                  <button
                    type="button"
                    class="tpf-kind-switch"
                    role="switch"
                    data-testid="tp-only-matching-kind"
                    [class.tpf-kind-switch--on]="onlyMatchingKind()"
                    [attr.aria-checked]="onlyMatchingKind()"
                    [attr.title]="
                      (onlyMatchingKind()
                        ? 'testPlans.form.picker.onlyMatchingOn'
                        : 'testPlans.form.picker.onlyMatchingOff'
                      ) | translate
                    "
                    (click)="onlyMatchingKind.set(!onlyMatchingKind())"
                  >
                    <span class="tpf-kind-switch__track" aria-hidden="true"></span>
                    {{
                      (planKind() === 'AUTOMATED'
                        ? 'testPlans.form.picker.onlyAutomated'
                        : 'testPlans.form.picker.onlyManual'
                      ) | translate
                    }}
                  </button>
                  @if (planKind() === 'MANUAL') {
                    <select
                      class="tpf-picker-select"
                      [class.tpf-picker-select--active]="executionFilter() === 'AUTOMATABLE'"
                      data-testid="tp-automatable-filter"
                      [value]="executionFilter() === 'AUTOMATABLE' ? 'AUTOMATABLE' : ''"
                      (change)="onExecutionFilterChange($event)"
                      [attr.aria-label]="'testPlans.form.picker.automatableAria' | translate"
                    >
                      <option value="">{{ 'testPlans.form.picker.allManual' | translate }}</option>
                      <option value="AUTOMATABLE">{{ 'testPlans.form.picker.onlyAutomatable' | translate }}</option>
                    </select>
                  }
                }
                <select
                  class="tpf-picker-select"
                  [class.tpf-picker-select--active]="testTypeFilter()"
                  [value]="testTypeFilter()"
                  (change)="onTestTypeChange($event)"
                  [attr.aria-label]="'testPlans.form.picker.typeAria' | translate"
                >
                  <option value="">{{ 'testPlans.form.picker.allTypes' | translate }}</option>
                  @for (t of availableTestTypes(); track t) {
                    <option [value]="t">{{ t | tcValueLabel: 'testType' }}</option>
                  }
                </select>
                <select
                  class="tpf-picker-select"
                  [class.tpf-picker-select--active]="priorityFilter()"
                  [value]="priorityFilter()"
                  (change)="onPriorityChange($event)"
                  [attr.aria-label]="'testPlans.form.picker.priorityAria' | translate"
                >
                  <option value="">{{ 'testPlans.form.picker.allPriorities' | translate }}</option>
                  @for (p of availablePriorities(); track p) {
                    <option [value]="p">{{ 'testCases.priority.' + p | translate }}</option>
                  }
                </select>
                <app-tag-filter
                  data-testid="tp-tag-filter"
                  size="sm"
                  [tags]="tagOptions()"
                  [value]="tagFilter()"
                  [ariaLabel]="'testPlans.form.picker.tagsAria' | translate"
                  (valueChange)="onTagFilterChange($event)"
                />
                <app-saved-views-menu
                  data-testid="picker-saved-views"
                  [views]="savedViewsOptions()"
                  [activeName]="activeSavedViewName()"
                  [context]="'TEST_PLAN_PICKER'"
                  [projectId]="projectId"
                  (viewSelected)="onSavedViewSelected($event)"
                  (viewSaved)="onSavedViewSaved($event)"
                  (viewDeleted)="onSavedViewDeleted($event)"
                  (viewDeleteFailed)="onSavedViewDeleteFailed()"
                />
                @if (tagFilter().length > 0 && ruleTags().length === 0 && !isReadOnly()) {
                  <button
                    type="button"
                    class="tpf-rule-adopt"
                    data-testid="tp-adopt-rule"
                    [disabled]="saving()"
                    [attr.title]="'testPlans.form.rule.adoptTitle' | translate"
                    (click)="adoptRule()"
                  >
                    {{ 'testPlans.form.rule.adopt' | translate }}
                  </button>
                }
              </div>
              @if (savedViewsError()) {
                <p class="tpf-picker-error" role="alert">{{ savedViewsError() }}</p>
              }
            }

            @if (ruleTags().length > 0) {
              <div class="tpf-rule-banner" [class.tpf-rule-banner--list]="!addMode()" data-testid="tp-selection-rule">
                <span class="tpf-rule-banner__text">
                  <strong>{{ 'testPlans.form.rule.bannerTitle' | translate }}</strong>
                  {{ 'testPlans.form.rule.bannerBefore' | translate }}
                  @for (tag of ruleTags(); track tag) {
                    <code>{{ tag }}</code>
                  }
                  {{ 'testPlans.form.rule.bannerAfter' | translate }}
                </span>
                @if (!isReadOnly()) {
                  <button
                    type="button"
                    class="tpf-rule-banner__remove"
                    data-testid="tp-remove-rule"
                    [disabled]="saving()"
                    (click)="removeRule()"
                  >
                    {{ 'testPlans.form.rule.remove' | translate }}
                  </button>
                }
              </div>
            }

            @if (testCasesLoadError()) {
              <p class="tpf-picker-load-error" role="alert" data-testid="tp-testfaelle-load-error">
                {{ 'testPlans.form.picker.loadError' | translate }}
              </p>
            } @else {
              <div
                class="tpf-picker-table"
                [class.tpf-picker-table--list]="!addMode()"
                role="table"
                data-testid="tp-testfaelle"
                [attr.aria-label]="'testPlans.form.picker.tableAria' | translate"
              >
                <div class="tpf-picker-head-group" role="rowgroup">
                  <div class="tpf-picker-head" role="row">
                    <div
                      class="tpf-picker-col-check"
                      role="columnheader"
                      [attr.aria-label]="'testPlans.form.picker.columns.select' | translate"
                    >
                      @if (addMode() && visibleCandidates().length > 0) {
                        <input
                          type="checkbox"
                          class="tpf-picker-check"
                          data-testid="tp-select-all-kind"
                          [checked]="allOfKindSelected()"
                          [disabled]="saving()"
                          [attr.aria-label]="
                            (allOfKindSelected()
                              ? 'testPlans.form.picker.deselectAll'
                              : 'testPlans.form.picker.selectAll'
                            ) | translate
                          "
                          [attr.title]="
                            (allOfKindSelected()
                              ? 'testPlans.form.picker.deselectAll'
                              : 'testPlans.form.picker.selectAll'
                            ) | translate
                          "
                          (change)="onSelectAllChange($event)"
                        />
                      }
                    </div>
                    <div class="tpf-picker-col-id" role="columnheader">
                      {{ 'testPlans.form.picker.columns.id' | translate }}
                    </div>
                    <div class="tpf-picker-col-title" role="columnheader">
                      {{ 'testPlans.form.picker.columns.title' | translate }}
                    </div>
                    <div class="tpf-picker-col-execution" role="columnheader">
                      {{ 'testPlans.form.picker.columns.execution' | translate }}
                    </div>
                    <div class="tpf-picker-col-type" role="columnheader">
                      {{ 'testPlans.form.picker.columns.testType' | translate }}
                    </div>
                    <div
                      class="tpf-picker-col-req tpf-picker-head-req"
                      role="columnheader"
                      [attr.title]="'testPlans.form.picker.columns.requirement' | translate"
                    >
                      {{ 'testPlans.form.picker.columns.requirement' | translate }}
                    </div>
                    <div class="tpf-picker-col-steps" role="columnheader">
                      {{ 'testPlans.form.picker.columns.steps' | translate }}
                    </div>
                  </div>
                </div>

                <div
                  class="tpf-picker-list"
                  role="rowgroup"
                  tabindex="0"
                  [attr.aria-label]="'testPlans.form.picker.tableAria' | translate"
                >
                  @for (tc of visibleCandidates(); track tc.id; let first = $first, last = $last) {
                    <div
                      class="tpf-picker-row"
                      data-testid="tp-testfall-zeile"
                      [attr.data-case-id]="tc.id"
                      [class.tpf-picker-row--on]="isSelected(tc)"
                      [class.tpf-picker-row--locked]="isKindLocked(tc)"
                      [class.tpf-picker-row--readonly]="isReadOnly()"
                      role="row"
                      [attr.tabindex]="isReadOnly() ? -1 : 0"
                      [attr.title]="isKindLocked(tc) ? ('testPlans.form.kind.lockedTooltip' | translate) : null"
                      [attr.aria-disabled]="saving() || isKindLocked(tc) || isReadOnly() || null"
                      (click)="toggle(tc)"
                      (keydown.enter)="onRowKey($event, tc)"
                      (keydown.space)="onRowKey($event, tc)"
                    >
                      <div class="tpf-picker-col-check" role="cell">
                        @if (!isReadOnly()) {
                          <input
                            type="checkbox"
                            class="tpf-picker-check"
                            [checked]="isSelected(tc)"
                            [disabled]="saving()"
                            [attr.aria-label]="
                              'testPlans.form.picker.rowCheckboxAria' | translate: { id: tc.key ?? '', title: tc.title }
                            "
                            (click)="$event.stopPropagation()"
                            (change)="onRowCheckboxChange($event, tc)"
                          />
                        }
                      </div>
                      <div class="tpf-picker-col-id" role="cell">
                        @if (tc.key) {
                          <code class="tpf-picker-id">{{ tc.key }}</code>
                        }
                      </div>
                      <div class="tpf-picker-col-title" role="cell">
                        <span class="tpf-picker-title" [attr.title]="tc.title">{{ tc.title }}</span>
                        @if (tc.tags?.length) {
                          <span class="tpf-picker-tags">
                            @for (tag of (tc.tags ?? []).slice(0, 3); track tag) {
                              <span class="tpf-picker-tag" [class.tpf-picker-tag--on]="tagFilter().includes(tag)">
                                {{ tag }}
                              </span>
                            }
                            @if ((tc.tags ?? []).length > 3) {
                              <span class="tpf-picker-tag-more">+{{ (tc.tags ?? []).length - 3 }}</span>
                            }
                          </span>
                        }
                      </div>
                      <div class="tpf-picker-col-execution tpf-picker-execution" role="cell">
                        <span
                          class="tpf-picker-automation"
                          [class.tpf-picker-automation--auto]="caseExecutionKind(tc) === 'AUTOMATED'"
                        >
                          <span class="tpf-picker-automation__dot" aria-hidden="true"></span>
                          {{
                            (caseExecutionKind(tc) === 'AUTOMATED'
                              ? 'testPlans.form.picker.automated'
                              : 'testPlans.form.picker.manual'
                            ) | translate
                          }}
                        </span>
                        @if (tc.automationStatus === 'AUTOMATABLE') {
                          <span
                            class="tpf-picker-automatable"
                            data-testid="tp-automatable-hint"
                            [attr.title]="'testPlans.form.picker.automatableTitle' | translate"
                          >
                            {{ 'testPlans.form.picker.automatable' | translate }}
                          </span>
                        }
                      </div>
                      <div class="tpf-picker-col-type tpf-picker-type" role="cell">
                        {{ tc.testType | tcValueLabel: 'testType' }}
                      </div>
                      <div class="tpf-picker-col-req" role="cell">
                        <code class="tpf-picker-req" [attr.title]="tc.requirementLabel || null">
                          {{ tc.requirementLabel }}
                        </code>
                      </div>
                      <div class="tpf-picker-col-steps" role="cell">
                        @if (!addMode() && !isReadOnly()) {
                          <span class="tpf-picker-move">
                            <button
                              type="button"
                              data-testid="tp-move-up"
                              [disabled]="first || saving()"
                              [attr.aria-label]="'testPlans.form.picker.moveUp' | translate"
                              [attr.title]="'testPlans.form.picker.moveUp' | translate"
                              (click)="moveCase(tc, -1, $event)"
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              data-testid="tp-move-down"
                              [disabled]="last || saving()"
                              [attr.aria-label]="'testPlans.form.picker.moveDown' | translate"
                              [attr.title]="'testPlans.form.picker.moveDown' | translate"
                              (click)="moveCase(tc, 1, $event)"
                            >
                              ▼
                            </button>
                          </span>
                        }
                        <button
                          type="button"
                          class="tpf-picker-steps-toggle"
                          data-testid="tp-schritte-toggle"
                          [class.tpf-picker-steps-toggle--open]="isStepExpanded(tc)"
                          [attr.title]="'testPlans.form.picker.stepsTooltip' | translate"
                          [attr.aria-expanded]="isStepExpanded(tc)"
                          [attr.aria-label]="'testPlans.form.picker.stepsButtonAria' | translate: { title: tc.title }"
                          (click)="toggleStepsExpanded(tc, $event)"
                        >
                          @if (stepTotal(tc) === 1) {
                            {{ 'testPlans.form.picker.stepsButtonOne' | translate }}
                          } @else {
                            {{ 'testPlans.form.picker.stepsButton' | translate: { n: stepTotal(tc) } }}
                          }
                          <mat-icon fontSet="material-icons-outlined" aria-hidden="true">chevron_right</mat-icon>
                        </button>
                      </div>
                    </div>

                    @if (isStepExpanded(tc)) {
                      <div class="tpf-picker-steps-row" role="row">
                        <div class="tpf-picker-steps" role="cell" aria-colspan="7">
                          <div class="tpf-picker-steps__label">
                            {{ 'testPlans.form.picker.stepsHeading' | translate }}
                          </div>
                          @if (isStepLoading(tc)) {
                            <p class="tpf-picker-steps__hint">
                              {{ 'testPlans.form.picker.stepsLoading' | translate }}
                            </p>
                          } @else if (stepsFor(tc).length === 0) {
                            <p class="tpf-picker-steps__hint">
                              {{ 'testPlans.form.picker.stepsEmpty' | translate }}
                            </p>
                          } @else {
                            @for (step of stepsFor(tc); track step.id; let i = $index) {
                              <div class="tpf-step-row">
                                <span class="tpf-step-row__index">{{ i + 1 }}</span>
                                <span class="tpf-step-row__title">
                                  {{ step.title || step.action || ('testPlans.form.picker.stepUnnamed' | translate) }}
                                </span>
                              </div>
                            }
                          }
                        </div>
                      </div>
                    }
                  } @empty {
                    <div role="row">
                      <div class="tpf-picker-empty" role="cell" aria-colspan="7">
                        {{
                          (addMode() && candidatesCount() > 0
                            ? 'testPlans.form.picker.empty'
                            : 'testPlans.form.picker.selectedEmpty'
                          ) | translate
                        }}
                      </div>
                    </div>
                  }
                </div>
              </div>

              @if (addMode() && hiddenByKindCount() > 0) {
                <div class="tpf-hidden-by-kind" data-testid="tp-hidden-by-kind">
                  <span>
                    {{
                      (planKind() === 'AUTOMATED'
                        ? 'testPlans.form.picker.hiddenManual'
                        : 'testPlans.form.picker.hiddenAutomated'
                      ) | translate: { count: hiddenByKindCount() }
                    }}
                  </span>
                  <button type="button" data-testid="tp-show-other-kind" (click)="showOtherKind()">
                    {{ 'testPlans.form.picker.showOtherKind' | translate }}
                  </button>
                </div>
              }

              @if (!isReadOnly() && (!addMode() || isEdit)) {
                <div class="tpf-picker-modeswitch">
                  @if (!addMode()) {
                    <button
                      mat-stroked-button
                      type="button"
                      class="tpf-picker-modeswitch__add"
                      (click)="enterAddMode()"
                    >
                      <mat-icon aria-hidden="true">add</mat-icon>
                      {{ 'testPlans.form.picker.addMore' | translate }}
                    </button>
                  } @else {
                    <button
                      mat-stroked-button
                      type="button"
                      class="tpf-picker-modeswitch__apply"
                      [disabled]="!hasTestCases()"
                      (click)="exitAddMode()"
                    >
                      <mat-icon aria-hidden="true">check</mat-icon>
                      {{ 'testPlans.form.picker.applySelection' | translate }}
                    </button>
                  }
                </div>
              }

              <div class="tpf-step-footer" data-testid="tp-step-footer">
                <span class="tpf-step-footer__text">
                  @if (selectedCount() === 1) {
                    {{ 'testPlans.form.picker.selectedSubtitleOne' | translate }}
                  } @else if (hasTestCases()) {
                    {{ 'testPlans.form.picker.selectedSubtitle' | translate: { selected: selectedCount() } }}
                  } @else if (!isReadOnly()) {
                    {{ 'testPlans.form.steps.selectOne' | translate }}
                  }
                </span>
                <button
                  mat-flat-button
                  color="primary"
                  type="button"
                  data-testid="tp-step-next"
                  [disabled]="!hasTestCases()"
                  (click)="goToStep(2)"
                >
                  {{ 'testPlans.form.steps.next' | translate }}
                </button>
              </div>
            }
          </div>
        </div>

        @if (activeStep() === 2) {
          <div class="tpf-step-panel" role="tabpanel" id="tp-step-panel-2" aria-labelledby="tp-step-tab-2">
            <fieldset class="tpf-pakete" [disabled]="saving()">
              <app-testpakete-karte
                [testfaelle]="paketTestfaelle()"
                [release]="selectedRelease()"
                [ungespeichert]="dirty() || selectionDirty()"
                [ohneStartrecht]="isReadOnly()"
                [schreibgeschuetzt]="isReadOnly()"
                [umgebungAnlegbar]="umgebungAnlegbar()"
              />
            </fieldset>
            <div class="tpf-step-footer tpf-step-footer--plain" data-testid="tp-step-footer-2">
              <button mat-stroked-button type="button" data-testid="tp-step-back" (click)="goToStep(1)">
                {{ 'testPlans.form.steps.back' | translate }}
              </button>
              <span class="tpf-step-footer__text">
                @if (!isReadOnly()) {
                  {{
                    (isEdit ? 'testPlans.form.steps.optionalHintEdit' : 'testPlans.form.steps.optionalHintCreate')
                      | translate
                  }}
                }
              </span>
              @if (!isReadOnly()) {
                <button
                  mat-flat-button
                  color="primary"
                  type="button"
                  data-testid="tp-step-save"
                  [disabled]="saveDisabled()"
                  (click)="onSave()"
                >
                  {{ (isEdit ? 'testPlans.form.save' : 'testPlans.form.saveCreate') | translate }}
                </button>
              }
            </div>
          </div>
        }

        <app-test-plan-details-historie
          [projectId]="projectId"
          [planId]="planId"
          [readonly]="historieReadonly()"
          [personOptions]="personOptions()"
          [anleger]="anleger()"
          [notes]="notesValue()"
          [notesControl]="form.controls.notes"
        />
      </div>

      <aside class="tpf-sidebar">
        <div class="tpf-card">
          <div class="tpf-card__body tpf-card__body--sidebar">
            <div class="tpf-card__inline-header">{{ 'testPlans.form.sidebar.properties' | translate }}</div>
            <div class="tpf-sidebar__fields">
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="tpf-field tpf-sidebar__status">
                <mat-label>{{ 'testPlans.form.sidebar.status' | translate }}</mat-label>
                <mat-select formControlName="status" data-testid="tp-status">
                  @for (status of statusOptions; track status) {
                    <mat-option [value]="status">{{ 'testPlans.status.' + status | translate }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="tpf-field tpf-sidebar__status">
                <mat-label>{{ 'testPlans.form.sidebar.type' | translate }}</mat-label>
                <mat-select formControlName="type" data-testid="tp-plan-type">
                  @for (type of typeOptions; track type) {
                    <mat-option [value]="type">{{ 'testPlans.type.' + type | translate }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <div class="tpf-sidebar__assignee">
                <app-person-select
                  formControlName="assigneeUserId"
                  [label]="'testPlans.form.fields.assignee' | translate"
                  [options]="personOptions()"
                  [required]="true"
                  [allowNone]="false"
                />
                @if (form.controls.assigneeUserId.hasError('required') && form.controls.assigneeUserId.touched) {
                  <p class="tpf-sidebar__hint tpf-sidebar__hint--error">
                    {{ 'testPlans.form.fields.assigneeRequired' | translate }}
                  </p>
                }
              </div>
              <div class="tpf-tags-field">
                <label class="tpf-tags-field__label" for="tp-tags-input">
                  {{ 'testPlans.form.sidebar.tags' | translate }}
                </label>
                <div class="tpf-tags" data-testid="tp-tags" [class.tpf-tags--error]="tagsInvalid()">
                  @for (tag of tagsValue(); track tag) {
                    <span class="tpf-tags__chip">
                      <span class="tpf-tags__label">{{ tag }}</span>
                      <button
                        type="button"
                        class="tpf-tags__remove"
                        [disabled]="saving() || isReadOnly()"
                        [attr.aria-label]="'testPlans.form.sidebar.tagRemoveAria' | translate: { tag: tag }"
                        (click)="removeTag(tag)"
                      >
                        <mat-icon aria-hidden="true">close</mat-icon>
                      </button>
                    </span>
                  }
                  <input
                    #tagField
                    id="tp-tags-input"
                    type="text"
                    class="tpf-tags__input"
                    [disabled]="saving() || isReadOnly()"
                    [placeholder]="
                      tagsValue().length || isReadOnly() ? '' : ('testPlans.form.sidebar.tagsPlaceholder' | translate)
                    "
                    [attr.aria-invalid]="tagsInvalid() ? 'true' : null"
                    [attr.aria-describedby]="tagsInvalid() ? 'tp-tags-error' : null"
                    (input)="onTagInput(tagField)"
                    (keydown)="onTagKeydown($event, tagField)"
                    (blur)="onTagBlur(tagField)"
                  />
                </div>
                @if (tagsInvalid()) {
                  <p id="tp-tags-error" class="tpf-tags-field__error" role="alert" data-testid="tp-tags-error">
                    {{ 'testPlans.form.sidebar.tagsInvalid' | translate }}
                  </p>
                }
              </div>
            </div>
          </div>
        </div>

        <div class="tpf-card">
          <div class="tpf-card__body tpf-card__body--sidebar tpf-card__body--summary">
            <div class="tpf-card__inline-header">{{ 'testPlans.form.sidebar.summary' | translate }}</div>
            <div>
              <div class="tpf-meta-row">
                <span class="tpf-meta-row__label">{{ 'testPlans.form.sidebar.testCases' | translate }}</span>
                <span class="tpf-meta-row__value">{{ selectedCount() }}</span>
              </div>
              <div class="tpf-meta-row">
                <span class="tpf-meta-row__label">{{ 'testPlans.form.sidebar.release' | translate }}</span>
                <span class="tpf-meta-row__value" data-testid="tpf-summary-release">
                  {{ selectedReleaseLabel() ?? '—' }}
                </span>
              </div>
              <div class="tpf-meta-row">
                <span class="tpf-meta-row__label">{{ 'testPlans.form.sidebar.executionKind' | translate }}</span>
                <span class="tpf-meta-row__value">
                  @if (executionCounts(); as counts) {
                    @if (counts.manual && counts.automated) {
                      {{ 'testPlans.form.sidebar.executionManual' | translate: { count: counts.manual } }} ·
                      {{ 'testPlans.form.sidebar.executionAutomated' | translate: { count: counts.automated } }}
                    } @else if (counts.manual) {
                      {{ 'testPlans.form.sidebar.executionManual' | translate: { count: counts.manual } }}
                    } @else if (counts.automated) {
                      {{ 'testPlans.form.sidebar.executionAutomated' | translate: { count: counts.automated } }}
                    } @else {
                      —
                    }
                  }
                </span>
              </div>
            </div>
            <div class="tpf-sidebar__actions">
              @if (isEdit) {
                @if (canOpenResults()) {
                  <div class="tpf-sidebar__done" role="status">
                    <mat-icon fontSet="material-icons-outlined" aria-hidden="true">check_circle</mat-icon>
                    {{ 'testPlans.form.sidebar.done' | translate }}
                  </div>
                }
                @if (!isReadOnly()) {
                  <button
                    mat-flat-button
                    color="primary"
                    type="button"
                    class="tpf-sidebar__echtlauf"
                    data-testid="tp-echtlauf"
                    [disabled]="!!echtlaufBlock()"
                    [disabledInteractive]="true"
                    [attr.aria-describedby]="
                      echtlaufBlock() === 'mixed' || echtlaufBlock() === 'dirty' ? 'tp-echtlauf-hint' : null
                    "
                    (click)="onEchtlauf()"
                  >
                    <span class="tpf-sidebar__echtlauf-icon" aria-hidden="true">▶</span>
                    {{ 'testPlans.form.sidebar.echtlauf' | translate }}
                  </button>
                  @if (echtlaufBlock() === 'mixed') {
                    <p
                      id="tp-echtlauf-hint"
                      class="tpf-sidebar__echtlauf-hint tpf-sidebar__echtlauf-hint--mixed"
                      data-testid="tp-echtlauf-hint"
                    >
                      {{ 'testPlans.form.sidebar.echtlaufMixed' | translate }}
                    </p>
                  } @else if (echtlaufBlock() === 'dirty') {
                    <p id="tp-echtlauf-hint" class="tpf-sidebar__echtlauf-hint" data-testid="tp-echtlauf-hint">
                      {{ 'testPlans.form.sidebar.echtlaufDirty' | translate }}
                    </p>
                  }
                }
                <a
                  mat-stroked-button
                  class="tpf-sidebar__report"
                  data-testid="tp-bericht"
                  [routerLink]="['/projects', projectId, 'test-plans', planId, 'report']"
                >
                  <mat-icon fontSet="material-icons-outlined" aria-hidden="true">shield</mat-icon>
                  {{ 'testPlans.form.sidebar.report' | translate }}
                </a>
              } @else {
                <p class="tpf-sidebar__create-hint">{{ 'testPlans.form.sidebar.createHint' | translate }}</p>
              }
            </div>
          </div>
        </div>
      </aside>
    </form>

    @if (saveError()) {
      <p class="tpf-hint tpf-hint--error">{{ saveErrorKey() | translate }}</p>
    }
  }
</div>

@if (paketStore.dialog(); as paketDialog) {
  <app-paket-testfall-dialog
    [testfaelle]="paketTestfaelle()"
    [pakete]="paketBelegung()"
    [paketKey]="paketDialog.paketKey"
    (uebernehmen)="paketStore.uebernehmeDialog($event, selectedRelease())"
    (abgebrochen)="paketStore.schliesseDialog()"
  />
}

<app-confirm-dialog
  [visible]="!!pendingKindChange()"
  [title]="'testPlans.form.kind.changeTitle' | translate"
  [message]="
    (pendingKindChange() === 'MANUAL'
      ? 'testPlans.form.kind.changeRemovesAutomated'
      : 'testPlans.form.kind.changeRemovesManual'
    ) | translate: { count: pendingKindChangeCount() }
  "
  [confirmLabel]="'testPlans.form.kind.changeConfirm' | translate"
  [cancelLabel]="'testPlans.form.cancel' | translate"
  confirmTestId="tp-kind-change-confirm"
  icon="warning"
  (confirmed)="onKindChangeConfirmed()"
  (cancelled)="pendingKindChange.set(null)"
/>

@if (selectionDialog(); as selectionChange) {
  <app-selection-change-dialog
    data-testid="tp-change-selection-dialog"
    [added]="selectionChange.added"
    [removed]="selectionChange.removed"
    [runs]="selectionRuns()"
    [loading]="selectionRunsLoading()"
    [reopensPlan]="selectionChange.reopensPlan"
    [(reason)]="changeReason"
    (confirmed)="onSelectionDialogConfirmed()"
    (cancelled)="onSelectionDialogCancelled()"
  />
}

<lib-modal
  [closeLabel]="'general.close' | translate"
  [title]="'testPlans.form.discard.title' | translate"
  [text]="'testPlans.form.discard.message' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'testPlans.form.discard.confirm' | translate"
  [cancelText]="'testPlans.form.discard.cancel' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
